import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

const categories = [
  { label: "Movies & Series", to: "/explore", img: "/design/cat-movies.jpg" },
  { label: "Actors & Filmmakers", to: "/people", img: "/design/cat-actors.jpg" },
  { label: "Genres & Languages", to: "/topics/genres-languages", img: "/design/cat-genres.jpg" },
  { label: "Festivals & Awards", to: "/festivals", img: "/design/cat-festivals.jpg" },
  { label: "Cinema History", to: "/topics/halls-of-fame-history", img: "/design/cat-history.jpg" },
  { label: "News & Reviews", to: "/news", img: "/design/cat-news.jpg" },
];

const trendingStills = {
  "drive-my-car-2021": "/design/trend-1.jpg",
  "parasite-2019": "/design/trend-2.jpg",
  "rrr-2022": "/design/trend-3.jpg",
  "roma-2018": "/design/trend-4.jpg",
  "in-the-mood-for-love-2000": "/design/trend-5.jpg",
  "amelie-2001": "/design/trend-6.jpg",
  "cinema-paradiso-1988": "/design/cat-history.jpg",
  "spirited-away-2001": "/design/cat-genres.jpg",
  "city-of-god-2002": "https://images.pexels.com/photos/19374140/pexels-photo-19374140.jpeg?auto=compress&cs=tinysrgb&h=400&w=800",
  "lionheart-2018": "https://images.unsplash.com/photo-1701275998609-119e2c09f443?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "portrait-of-a-lady-on-fire-2019": "https://images.unsplash.com/photo-1604975701397-6365ccbd028a?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
};

const BLOCKS = {
  hero_sub1: "Discover global cinema by place, language, genre, and era.",
  hero_sub2: "From local stories to worldwide sensations — cinema connects us all.",
  world_title: "Cinema Around the World",
  world_sub: "Explore films, people, and stories from every corner of the globe.",
  trending_title: "Now Trending",
  trending_sub: "Fresh stories. Global voices. A more connected world.",
};

export default function Home() {
  const [trending, setTrending] = useState([]);
  const [articles, setArticles] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const blocks = useMemo(() => BLOCKS, []);
  const { lang, tr, busy } = usePageTranslation(blocks, "home");

  useEffect(() => {
    api.get("/movies", { params: { trending: true, limit: 12 } }).then((r) => setTrending(r.data));
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3)));
    api.get("/spotlight").then((r) => setSpotlight(r.data));
  }, []);

  const scrollRow = (dir) => {
    document.getElementById("trending-row")?.scrollBy({ left: dir * 700, behavior: "smooth" });
  };

  const speakText = [tr("hero_sub1", BLOCKS.hero_sub1), tr("hero_sub2", BLOCKS.hero_sub2), tr("world_title", BLOCKS.world_title), tr("world_sub", BLOCKS.world_sub)].join(" ");

  return (
    <main data-testid="home-page" className="bg-[#faf7f2]">
      {/* HERO — text left on white, design collage right */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-[minmax(420px,44%)_1fr] items-stretch">
          <div className="px-4 sm:px-8 py-14 lg:py-20 fade-up relative z-10">
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] text-[#171210]">
              Every Story. Every&nbsp;Screen.
              <span className="block text-[#7d1a1a]">Every Place.</span>
            </h1>
            <p className="mt-6 text-lg text-[#3d342c] max-w-lg leading-relaxed">
              {tr("hero_sub1", BLOCKS.hero_sub1)}
              <br />
              {tr("hero_sub2", BLOCKS.hero_sub2)}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/explore"
                data-testid="hero-explore-button"
                className="inline-flex items-center gap-2 bg-[#7d1a1a] text-[#f6f1e7] px-6 py-3 rounded-md font-display font-semibold hover:bg-[#5c1220] transition-colors"
              >
                <Globe className="w-4 h-4" /> Explore Global Cinema <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/countries"
                data-testid="hero-countries-button"
                className="inline-flex items-center gap-2 border border-[#7d1a1a] text-[#7d1a1a] px-6 py-3 rounded-md font-display font-semibold hover:bg-[#7d1a1a] hover:text-[#f6f1e7] transition-colors"
              >
                <MapPin className="w-4 h-4" /> Browse by Country
              </Link>
            </div>
            <p className="eyebrow mt-12" style={{ color: "#6b5d51" }}>Different Places. A Brighter Picture.</p>
            <div className="mt-6">
              <PageToolbar speakText={speakText} busy={busy} lang={lang} />
            </div>
          </div>
          <div className="relative min-h-[300px] lg:min-h-0">
            <img
              src="/design/hero-collage.jpg"
              alt="Cinema brings us closer — global cinema collage"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "62% center" }}
            />
            <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent hidden lg:block" />
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5">
          {categories.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              data-testid={`category-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="poster-card block bg-white border border-[#e2d9c8] shadow-sm"
            >
              <div className="aspect-[254/114] overflow-hidden bg-[#171210]">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="flex items-center justify-between px-4 py-3.5">
                <span className="font-display font-bold text-[17px] text-[#171210]">{c.label}</span>
                <ArrowRight className="w-4 h-4 text-[#171210] shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CINEMA AROUND THE WORLD */}
      <section className="bg-[#f4eee1] py-12 relative grain">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 grid lg:grid-cols-[320px_1fr_auto] gap-10 items-center">
          <div>
            <h2 className="font-display font-bold text-3xl sm:text-[40px] leading-tight text-[#171210]">{tr("world_title", BLOCKS.world_title)}</h2>
            <p className="mt-3 text-[#4a3f35]">{tr("world_sub", BLOCKS.world_sub)}</p>
          </div>
          <div className="relative flex items-center justify-center gap-6">
            <img src="/design/world-map.jpg" alt="World cinema map" className="max-h-40 mix-blend-multiply" />
            <div className="hidden md:block text-center rotate-[-6deg]">
              <p className="font-script text-3xl text-[#171210] leading-tight">Local Stories<br />Global Connections</p>
              <div className="h-1.5 w-32 bg-[#7d1a1a] mx-auto mt-1 rounded-full" style={{ clipPath: "polygon(0 40%, 100% 0, 98% 100%, 2% 90%)" }} />
            </div>
          </div>
          <div className="flex items-center">
            {[
              ["195+", "Countries"],
              ["4,000+", "Cities"],
              ["100+", "Languages"],
              ["Infinite", "Stories"],
            ].map(([v, l], i) => (
              <div key={l} className={`text-center px-6 lg:px-8 ${i > 0 ? "border-l border-[#cbbfa9]" : ""}`}>
                <p className="font-display font-bold text-3xl lg:text-4xl text-[#7d1a1a] whitespace-nowrap">{v}</p>
                <p className="text-sm text-[#4a3f35] mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CINEMA OF THE WEEK */}
      {spotlight && (
        <section data-testid="spotlight-section" className="bg-[#171210] text-[#f6f1e7] relative overflow-hidden">
          {spotlight.photo && (
            <img src={spotlight.photo.url} alt={spotlight.photo.alt} className="absolute inset-0 w-full h-full object-cover opacity-25" />
          )}
          <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 py-14 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <p className="eyebrow" style={{ color: "#e8b96a" }}>Cinema of the Week · Week {spotlight.week}</p>
              <h2 className="font-display font-bold text-3xl sm:text-5xl mt-2">
                {spotlight.country.flag} {spotlight.country.name}
              </h2>
              <p className="font-script text-2xl text-[#e8b96a] mt-1">{spotlight.country.industry}</p>
              <p className="mt-4 text-[#e9e2d4] leading-relaxed max-w-2xl line-clamp-3">{spotlight.country.overview}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/country/${spotlight.country.code}`}
                  data-testid="spotlight-country-link"
                  className="inline-flex items-center gap-2 bg-[#7d1a1a] text-[#f6f1e7] px-5 py-2.5 rounded-md font-display font-semibold hover:bg-[#a32222] transition-colors"
                >
                  Explore {spotlight.country.name} Cinema <ArrowRight className="w-4 h-4" />
                </Link>
                {spotlight.cities.map((c) => (
                  <Link key={c.id} to={`/city/${c.id}`} data-testid={`spotlight-city-${c.id}`} className="border border-[#5a4c3e] rounded-md px-4 py-2.5 text-sm hover:border-[#e8b96a] hover:text-[#e8b96a] transition-colors">
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar">
              {spotlight.movies.map((m) => (
                <Link key={m.id} to={`/movie/${m.id}`} data-testid={`spotlight-movie-${m.id}`} className="w-36 shrink-0 group">
                  <div className="aspect-[3/4] relative overflow-hidden border border-[#5a4c3e]" style={{ background: `linear-gradient(155deg, ${m.palette?.[0] || "#2a2019"}, ${m.palette?.[1] || "#7d1a1a"})` }}>
                    {m.image && <img src={m.image} alt={m.title} className="absolute inset-0 w-full h-full object-cover opacity-80" loading="lazy" />}
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="font-display font-semibold text-sm leading-tight group-hover:text-[#e8b96a] transition-colors">{m.title}</p>
                      <p className="font-mono-label text-[9px] text-[#d6cebf] mt-0.5">{m.year}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NOW TRENDING */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-8 py-12">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-display font-bold text-3xl sm:text-[40px] text-[#171210]">{tr("trending_title", BLOCKS.trending_title)}</h2>
            <p className="text-[#6b5d51] mt-2">{tr("trending_sub", BLOCKS.trending_sub)}</p>
          </div>
          <Link to="/explore" data-testid="see-all-trending-link" className="font-display text-sm font-semibold link-underline text-[#171210]">
            See All Trending →
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-[auto_1fr] gap-4 items-end">
          <div className="hidden sm:flex flex-col gap-2 pb-1">
            <button data-testid="trending-prev" onClick={() => scrollRow(-1)} className="w-10 h-10 rounded-full border border-[#cbbfa9] bg-white flex items-center justify-center hover:bg-[#171210] hover:text-white transition-colors" aria-label="Previous">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="trending-next" onClick={() => scrollRow(1)} className="w-10 h-10 rounded-full bg-[#171210] text-white flex items-center justify-center hover:bg-[#7d1a1a] transition-colors" aria-label="Next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div id="trending-row" className="flex gap-5 overflow-x-auto no-scrollbar pb-1">
            {trending.map((m) => (
              <Link key={m.id} to={`/movie/${m.id}`} data-testid={`trending-card-${m.id}`} className="poster-card block w-60 shrink-0 bg-white border border-[#e2d9c8] shadow-sm">
                <div className="aspect-[222/93] overflow-hidden bg-[#171210]">
                  <img src={trendingStills[m.id] || m.image || "/design/trend-1.jpg"} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="px-4 py-3">
                  <p className="font-display font-bold text-[16px] text-[#171210] truncate">{m.title}</p>
                  <p className="font-mono-label text-[10px] tracking-[0.12em] text-[#6b5d51] mt-0.5">
                    {m.genres?.[0]} &nbsp;|&nbsp; {m.country}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-8 pb-8">
        <div className="rule-thick pt-6 flex items-end justify-between mb-6">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#171210]">From the Editorial Desk</h2>
          <Link to="/news" data-testid="home-all-news-link" className="font-display text-sm font-semibold link-underline text-[#7d1a1a]">All News →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <Link key={a.id} to={`/news/${a.id}`} data-testid={`home-article-${a.id}`} className="group bg-white border border-[#e2d9c8] poster-card shadow-sm">
              {a.photo && (
                <div className="aspect-[16/9] overflow-hidden bg-[#171210]">
                  <img src={a.photo.url} alt={a.photo.alt} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-5">
                <p className="eyebrow">{a.category}</p>
                <h3 className="font-display font-bold text-lg mt-2 leading-snug group-hover:text-[#7d1a1a] transition-colors">{a.title}</h3>
                <p className="text-sm text-[#6b5d51] mt-2 line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
